// src/utils/contentAnalysis.js
// Content analysis service for smart design generation

/**
 * Analyzes an image to determine optimal design characteristics
 * @param {string} imageUrl - URL of the image to analyze
 * @param {object} options - Analysis options
 * @returns {Promise<object>} Analysis results
 */
export const analyzeImageContent = async (imageUrl, options = {}) => {
  console.log('Analyzing image content:', imageUrl);
  
  try {
    // For now, we'll use basic image analysis
    // Later, we can integrate with AI services like Google Vision, AWS Rekognition, etc.
    const analysis = await performBasicImageAnalysis(imageUrl);
    
    return {
      success: true,
      analysis: {
        // Content type detection
        contentType: determineContentType(analysis),
        
        // Layout suggestions
        suggestedLayout: determineBestLayout(analysis),
        
        // Design characteristics
        characteristics: {
          isPortrait: analysis.aspectRatio < 1,
          isLandscape: analysis.aspectRatio > 1,
          isSquare: Math.abs(analysis.aspectRatio - 1) < 0.1,
          brightness: analysis.brightness || 'medium',
          contrast: analysis.contrast || 'medium',
          peopleCount: analysis.peopleCount || 1
        },
        
        // Sport detection (basic)
        sport: detectSport(analysis),
        
        // Design recommendations
        recommendations: generateDesignRecommendations(analysis),
        
        // Confidence scores
        confidence: {
          contentType: analysis.confidence?.contentType || 0.8,
          layout: analysis.confidence?.layout || 0.7,
          sport: analysis.confidence?.sport || 0.6
        }
      }
    };
  } catch (error) {
    console.error('Error analyzing image content:', error);
    return {
      success: false,
      error: error.message,
      // Fallback analysis for graceful degradation
      analysis: getDefaultAnalysis()
    };
  }
};

/**
 * Performs basic image analysis using browser APIs
 */
const performBasicImageAnalysis = async (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        // Create canvas to analyze image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // Basic analysis
        const aspectRatio = img.width / img.height;
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const brightness = calculateBrightness(imageData);
        const contrast = calculateContrast(imageData);
        
        // Detect if likely a person photo based on aspect ratio and dimensions
        const isProbablyPerson = aspectRatio > 0.6 && aspectRatio < 1.4 && img.height > img.width * 0.8;
        
        resolve({
          width: img.width,
          height: img.height,
          aspectRatio,
          brightness,
          contrast,
          isProbablyPerson,
          // Estimate people count based on aspect ratio and size
          peopleCount: aspectRatio > 1.5 ? Math.min(3, Math.floor(aspectRatio * 1.5)) : 1,
          confidence: {
            contentType: isProbablyPerson ? 0.8 : 0.6,
            layout: 0.7,
            sport: 0.5 // Low confidence without actual AI detection
          }
        });
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image for analysis'));
    img.src = imageUrl;
  });
};

/**
 * Determines the best content type based on analysis
 */
const determineContentType = (analysis) => {
  // Logic to determine if this should be a photo, text, or video design
  if (analysis.isProbablyPerson) {
    return 'image'; // Photo-based design
  }
  
  // If very wide aspect ratio, might be good for text overlay
  if (analysis.aspectRatio > 2) {
    return 'type'; // Text-based design
  }
  
  // Default to image design
  return 'image';
};

/**
 * Determines the best layout based on image characteristics
 */
const determineBestLayout = (analysis) => {
  if (analysis.aspectRatio < 0.8) {
    return 'portrait_focus'; // Tall images work well with portrait-focused layouts
  } else if (analysis.aspectRatio > 1.5) {
    return 'landscape_action'; // Wide images good for action/team layouts
  } else if (analysis.peopleCount > 1) {
    return 'team_layout'; // Multiple people suggest team layouts
  }
  
  return 'standard_player'; // Default player card layout
};

/**
 * Basic sport detection (can be enhanced with AI later)
 */
const detectSport = (analysis) => {
  // For now, default to baseball since that's your main sport
  // Later, this could use AI to detect equipment, uniforms, field markings, etc.
  return {
    primary: 'baseball',
    confidence: 0.5, // Low confidence without actual AI detection
    alternatives: ['softball', 'general']
  };
};

/**
 * Generate design recommendations based on analysis
 */
const generateDesignRecommendations = (analysis) => {
  const recommendations = [];
  
  if (analysis.isProbablyPerson) {
    recommendations.push({
      type: 'player_card',
      reason: 'Perfect for individual player showcase',
      confidence: 0.9
    });
  }
  
  if (analysis.aspectRatio > 1.3) {
    recommendations.push({
      type: 'action_shot',
      reason: 'Wide format ideal for action photography',
      confidence: 0.8
    });
  }
  
  if (analysis.peopleCount > 1) {
    recommendations.push({
      type: 'team_poster',
      reason: 'Multiple people detected - great for team designs',
      confidence: 0.85
    });
  }
  
  // Always include a versatile option
  recommendations.push({
    type: 'versatile_layout',
    reason: 'Adaptable design that works with most content',
    confidence: 0.7
  });
  
  return recommendations;
};

/**
 * Calculate image brightness
 */
const calculateBrightness = (imageData) => {
  const data = imageData.data;
  let total = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    // Calculate luminance using standard formula
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    total += luminance;
  }
  
  const avgBrightness = total / (data.length / 4);
  
  if (avgBrightness < 85) return 'dark';
  if (avgBrightness > 170) return 'bright';
  return 'medium';
};

/**
 * Calculate image contrast
 */
const calculateContrast = (imageData) => {
  const data = imageData.data;
  const luminances = [];
  
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    luminances.push(luminance);
  }
  
  // Calculate standard deviation as a measure of contrast
  const mean = luminances.reduce((sum, val) => sum + val, 0) / luminances.length;
  const variance = luminances.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / luminances.length;
  const stdDev = Math.sqrt(variance);
  
  if (stdDev < 30) return 'low';
  if (stdDev > 60) return 'high';
  return 'medium';
};

/**
 * Provides default analysis for fallback scenarios
 */
const getDefaultAnalysis = () => ({
  contentType: 'image',
  suggestedLayout: 'standard_player',
  characteristics: {
    isPortrait: true,
    isLandscape: false,
    isSquare: false,
    brightness: 'medium',
    contrast: 'medium',
    peopleCount: 1
  },
  sport: {
    primary: 'baseball',
    confidence: 0.5,
    alternatives: ['softball', 'general']
  },
  recommendations: [
    {
      type: 'versatile_layout',
      reason: 'Safe default that works with most content',
      confidence: 0.7
    }
  ],
  confidence: {
    contentType: 0.5,
    layout: 0.5,
    sport: 0.5
  }
});

/**
 * Enhanced analysis using AI services (future implementation)
 */
export const analyzeImageWithAI = async (imageUrl, provider = 'openai') => {
  // Future implementation for AI-powered analysis
  // Could integrate with:
  // - OpenAI Vision API
  // - Google Cloud Vision
  // - AWS Rekognition
  // - Custom trained models
  
  console.log(`AI analysis with ${provider} - coming soon`);
  return getDefaultAnalysis();
};

/**
 * Categorize templates based on analysis results
 */
export const categorizeTemplatesForContent = (templates, analysis) => {
  return templates.map(template => {
    // Score each template based on how well it matches the content
    const score = calculateTemplateScore(template, analysis);
    
    return {
      ...template,
      smartScore: score,
      matchReason: getMatchReason(template, analysis, score),
      isRecommended: score > 0.7
    };
  }).sort((a, b) => b.smartScore - a.smartScore);
};

/**
 * Calculate how well a template matches the analyzed content
 */
const calculateTemplateScore = (template, analysis) => {
  let score = 0.5; // Base score
  
  // Boost score based on template name/category matching analysis
  const templateName = (template.name || '').toLowerCase();
  const templateCategory = (template.style_category || '').toLowerCase();
  
  // Content type matching
  if (analysis.contentType === 'image') {
    if (templateName.includes('player') || templateName.includes('portrait')) {
      score += 0.2;
    }
  }
  
  // Layout matching
  if (analysis.suggestedLayout === 'portrait_focus' && templateName.includes('portrait')) {
    score += 0.2;
  } else if (analysis.suggestedLayout === 'team_layout' && templateName.includes('team')) {
    score += 0.2;
  }
  
  // Sport matching
  if (analysis.sport?.primary === 'baseball' && 
      (templateCategory.includes('baseball') || template.style_subcategory === 'Baseball/Softball')) {
    score += 0.1;
  }
  
  return Math.min(1.0, score);
};

/**
 * Generate explanation for why a template matches
 */
const getMatchReason = (template, analysis, score) => {
  if (score > 0.8) {
    return 'Perfect match for your content type and layout';
  } else if (score > 0.6) {
    return 'Great fit based on image characteristics';
  } else if (score > 0.4) {
    return 'Good option that will work well';
  }
  return 'Versatile design suitable for various content';
};

/**
 * Analyzes image content with enhanced event tag context
 * @param {string} imageUrl - URL of the image to analyze
 * @param {object} eventTags - Event metadata and tags for context
 * @returns {Promise<object>} - Enhanced analysis results with event context
 */
export const analyzeImageContentWithEventTags = async (imageUrl, eventTags = {}) => {
  console.log('🔍 Starting enhanced content analysis with event tags:', eventTags);
  
  try {
    // First do basic image analysis
    const basicAnalysis = await analyzeImageContent(imageUrl);
    
    if (!basicAnalysis.success) {
      return basicAnalysis;
    }
    
    // Enhance analysis with event tag context
    const enhancedAnalysis = enhanceAnalysisWithEventTags(basicAnalysis.analysis, eventTags);
    
    return {
      success: true,
      analysis: enhancedAnalysis,
      eventTagsUsed: eventTags,
      enhancementApplied: true
    };
    
  } catch (error) {
    console.error('Error in enhanced content analysis:', error);
    return {
      success: false,
      error: error.message,
      analysis: getDefaultAnalysisWithEventTags(eventTags)
    };
  }
};

/**
 * Enhances basic analysis with event tag context
 */
const enhanceAnalysisWithEventTags = (basicAnalysis, eventTags) => {
  const enhanced = { ...basicAnalysis };
  
  // Enhanced sport detection
  if (eventTags.sport || eventTags.sport_type) {
    enhanced.sport = {
      primary: eventTags.sport || eventTags.sport_type,
      confidence: 0.95, // High confidence from event tags
      source: 'event_tags',
      detected: basicAnalysis.sport?.primary || 'baseball'
    };
  }
  
  // Enhanced people detection with team context
  if (eventTags.team_size || eventTags.player_count) {
    enhanced.characteristics.peopleCount = parseInt(eventTags.team_size || eventTags.player_count);
    enhanced.characteristics.isTeamPhoto = enhanced.characteristics.peopleCount > 1;
  }
  
  // Enhanced layout suggestions based on event type
  enhanced.suggestedLayout = determineSuggestedLayoutWithEventTags(enhanced, eventTags);
  
  // Add event-specific characteristics
  enhanced.eventContext = {
    level: eventTags.level || eventTags.competitive_level || 'youth',
    season: eventTags.season || eventTags.year || '2024',
    eventType: eventTags.event_type || eventTags.type || 'regular_season',
    venue: eventTags.venue || eventTags.location,
    timeOfDay: eventTags.time_of_day || eventTags.when,
    weather: eventTags.weather || eventTags.conditions
  };
  
  // Enhanced mood and style suggestions
  enhanced.moodSuggestions = determineMoodFromEventTags(eventTags);
  
  // Enhanced confidence scores
  enhanced.confidence = {
    ...enhanced.confidence,
    overall: Math.min(1.0, (enhanced.confidence.overall + 0.3)), // Boost with event context
    sport: enhanced.sport.confidence,
    layout: 0.9, // High confidence with event context
    eventContext: 0.95
  };
  
  // Enhanced recommendations with event awareness
  enhanced.recommendations = generateEnhancedRecommendations(enhanced, eventTags);
  
  console.log('✅ Enhanced analysis complete with event context');
  return enhanced;
};

/**
 * Determines suggested layout using both image analysis and event context
 */
const determineSuggestedLayoutWithEventTags = (analysis, eventTags) => {
  const baseLayout = analysis.suggestedLayout;
  const characteristics = analysis.characteristics;
  
  // Event-driven layout decisions
  if (eventTags.focus === 'stats' || eventTags.include_stats) {
    return 'stats_showcase';
  }
  
  if (eventTags.event_type === 'championship' || eventTags.achievement) {
    return 'achievement_highlight';
  }
  
  if (eventTags.photo_type === 'team' || characteristics.peopleCount > 1) {
    return 'team_collage';
  }
  
  if (eventTags.photo_type === 'action' || eventTags.action_type) {
    return characteristics.isPortrait ? 'action_portrait' : 'landscape_action_shot';
  }
  
  if (eventTags.photo_type === 'headshot' || eventTags.individual_focus) {
    return 'portrait_player_card';
  }
  
  // Fall back to image-based suggestion
  return baseLayout;
};

/**
 * Determines mood and style from event tags
 */
const determineMoodFromEventTags = (eventTags) => {
  const moods = [];
  
  // Event-based moods
  if (eventTags.event_type === 'championship') {
    moods.push('triumphant', 'celebratory', 'elite');
  } else if (eventTags.event_type === 'tournament') {
    moods.push('competitive', 'intense', 'focused');
  } else if (eventTags.event_type === 'practice') {
    moods.push('developmental', 'team-focused', 'improvement');
  }
  
  // Level-based moods
  if (eventTags.level === 'professional') {
    moods.push('elite', 'polished', 'dynamic');
  } else if (eventTags.level === 'college') {
    moods.push('spirited', 'competitive', 'energetic');
  } else if (eventTags.level === 'youth') {
    moods.push('fun', 'developmental', 'encouraging');
  }
  
  // Season/timing moods
  if (eventTags.season_phase === 'playoffs') {
    moods.push('intense', 'high-stakes', 'dramatic');
  } else if (eventTags.season_phase === 'opening') {
    moods.push('fresh', 'optimistic', 'new-beginnings');
  }
  
  // Weather/venue moods
  if (eventTags.venue === 'home') {
    moods.push('pride', 'community', 'support');
  } else if (eventTags.venue === 'away') {
    moods.push('determined', 'unified', 'resilient');
  }
  
  return moods.length > 0 ? moods : ['energetic', 'athletic', 'proud'];
};

/**
 * Generates enhanced recommendations using event context
 */
const generateEnhancedRecommendations = (analysis, eventTags) => {
  const recommendations = [];
  
  // Sport-specific recommendations with event context
  const sport = analysis.sport.primary;
  const level = eventTags.level || 'youth';
  const eventType = eventTags.event_type || 'regular';
  
  recommendations.push({
    type: 'layout',
    suggestion: analysis.suggestedLayout,
    reason: `Perfect for ${sport} ${eventType} at ${level} level`,
    priority: 'high',
    confidence: 0.9
  });
  
  // Background recommendations
  if (eventTags.venue === 'home') {
    recommendations.push({
      type: 'background',
      suggestion: 'home_venue_pride',
      reason: 'Home game atmosphere with team pride elements',
      priority: 'medium',
      confidence: 0.8
    });
  }
  
  // Text and styling recommendations
  if (eventTags.team_colors && eventTags.team_colors.length > 0) {
    recommendations.push({
      type: 'colors',
      suggestion: eventTags.team_colors,
      reason: 'Use team colors for brand consistency',
      priority: 'high',
      confidence: 0.95
    });
  }
  
  // Achievement highlighting
  if (eventTags.achievements && eventTags.achievements.length > 0) {
    recommendations.push({
      type: 'content',
      suggestion: 'highlight_achievements',
      reason: 'Feature player/team achievements prominently',
      priority: 'high',
      confidence: 0.9
    });
  }
  
  return recommendations;
};

/**
 * Returns default analysis enhanced with event tags when image analysis fails
 */
const getDefaultAnalysisWithEventTags = (eventTags) => {
  return {
    sport: {
      primary: eventTags.sport || eventTags.sport_type || 'baseball',
      confidence: 0.7,
      source: 'event_tags_fallback'
    },
    characteristics: {
      aspectRatio: 4/3,
      isPortrait: false,
      isLandscape: true,
      brightness: 'medium',
      peopleCount: parseInt(eventTags.team_size || eventTags.player_count || '1'),
      isTeamPhoto: (eventTags.team_size || eventTags.player_count) > 1
    },
    suggestedLayout: eventTags.photo_type === 'team' ? 'team_collage' : 'portrait_player_card',
    eventContext: {
      level: eventTags.level || 'youth',
      season: eventTags.season || '2024',
      eventType: eventTags.event_type || 'regular_season'
    },
    confidence: {
      overall: 0.6,
      sport: 0.7,
      layout: 0.8,
      eventContext: 0.9
    },
    source: 'event_tags_enhanced_fallback'
  };
};

/**
 * Validates and normalizes event tags for analysis
 * @param {object} rawEventTags - Raw event tags from your system
 * @returns {object} - Normalized event tags
 */
export const normalizeEventTags = (rawEventTags) => {
  return {
    // Core sport and team info
    sport: rawEventTags.sport || rawEventTags.sport_type || rawEventTags.league_sport,
    team_name: rawEventTags.team_name || rawEventTags.team,
    team_colors: rawEventTags.team_colors || rawEventTags.colors,
    level: rawEventTags.level || rawEventTags.competitive_level || rawEventTags.division,
    
    // Player info
    player_name: rawEventTags.player_name || rawEventTags.name,
    player_number: rawEventTags.player_number || rawEventTags.number || rawEventTags.jersey,
    position: rawEventTags.position || rawEventTags.role,
    player_count: rawEventTags.player_count || rawEventTags.team_size,
    
    // Event details
    event_type: rawEventTags.event_type || rawEventTags.type || rawEventTags.game_type,
    season: rawEventTags.season || rawEventTags.year,
    season_phase: rawEventTags.season_phase || rawEventTags.phase,
    venue: rawEventTags.venue || rawEventTags.location || rawEventTags.home_away,
    
    // Photo characteristics
    photo_type: rawEventTags.photo_type || rawEventTags.shot_type || rawEventTags.style,
    action_type: rawEventTags.action_type || rawEventTags.play_type,
    focus: rawEventTags.focus || rawEventTags.emphasis,
    include_stats: rawEventTags.include_stats || rawEventTags.show_stats,
    
    // Context and mood
    achievements: rawEventTags.achievements || rawEventTags.awards || [],
    time_of_day: rawEventTags.time_of_day || rawEventTags.when,
    weather: rawEventTags.weather || rawEventTags.conditions,
    mood: rawEventTags.mood || rawEventTags.tone,
    
    // Technical preferences
    background_preference: rawEventTags.background_preference || rawEventTags.bg_style,
    text_style: rawEventTags.text_style || rawEventTags.font_style,
    design_intensity: rawEventTags.design_intensity || rawEventTags.complexity || 'medium'
  };
}; 